import { computed, ref, watch } from 'vue'

import type { ComputedRef } from 'vue'
import type { BannerUpsellOfferContent } from '@ts-upsell-banner/interfaces'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import { AppRoute } from '@ts-routes/enums'

import { useOptionsStore } from '@stores/options'
import { useUpsellBannerStore } from '@stores/upsell-banner'

import { useKidRoutes } from '@composables/routes/kid-route.hook'
import { useTimeCountdown } from '@hooks/options/time-countdown.hook'
import { useUpsellBannerDismissApi } from '@api/upsell-banner/upsell-banner-dismiss.hook'

import { UPSELL_CAMPAIGN } from '@config/upsell-campaign'
import amplitudeConfig from '@config/amplitude.json'
import { resolveKidId } from '@helpers/kid-id'

import { trackEvent } from '@tools/amplitude'

const DISCIPLINE_LABEL: Record<Discipline, string> = {
  [Discipline.ELA]: 'ELA',
  [Discipline.Math]: 'Math',
}

interface BannerUpsellReturn {
  title: string
  titleMobile: string
  isVisible: ComputedRef<boolean>
  offer: ComputedRef<BannerUpsellOfferContent>
  ctaLabel: ComputedRef<string>
  href: ComputedRef<string | null>
  countdownText: ComputedRef<string>
  setSubmit: () => Promise<void>
}

export const useBannerUpsell = (): BannerUpsellReturn => {
  const optionsStore = useOptionsStore()
  const upsellBannerStore = useUpsellBannerStore()

  const {
    isRoutePricingEla,
    isRoutePricingMath,
    isRoutePadEla,
    isRoutePadMath,
    getHomeEla,
    getHomeMath,
  } = useKidRoutes()

  const { dismissUpsellBanner } = useUpsellBannerDismissApi()

  const name = UPSELL_CAMPAIGN.analyticsName
  const title = UPSELL_CAMPAIGN.title
  const titleMobile = UPSELL_CAMPAIGN.titleMobile

  const isEla = computed<boolean>(() => upsellBannerStore.getDiscipline === Discipline.ELA)
  const disciplineLabel = computed<string>(() => {
    const discipline = upsellBannerStore.getDiscipline
    return discipline ? DISCIPLINE_LABEL[discipline] : ''
  })
  const discount = computed<number>(() => optionsStore.getUpsellCampaignDiscount)
  const kidPhrase = computed<string>(() => upsellBannerStore.getKidName ? `for ${upsellBannerStore.getKidName}` : '')

  const offer = computed<BannerUpsellOfferContent>(() => upsellBannerStore.isCompletedDemo
    ? {
      lead: `Get ${discount.value}% off ${disciplineLabel.value} lessons with code`,
      kid: kidPhrase.value,
      code: UPSELL_CAMPAIGN.promoCode,
    }
    : {
      lead: `Save ${discount.value}% on the ${disciplineLabel.value} program`,
      kid: kidPhrase.value,
      code: null,
    })

  const ctaLabel = computed<string>(() => upsellBannerStore.isCompletedDemo
    ? `Add ${disciplineLabel.value}`
    : 'Try for free')

  const href = computed<string | null>(() => {
    const kidId = upsellBannerStore.getKidId
    if (!kidId) return null

    if (upsellBannerStore.isCompletedDemo) {
      return resolveKidId(isEla.value ? AppRoute.PricingEla : AppRoute.PricingMath, kidId)
    }

    return isEla.value ? getHomeEla(kidId) : getHomeMath(kidId)
  })

  const isUpsoldDisciplinePage = computed<boolean>(() => isEla.value
    ? (isRoutePricingEla.value || isRoutePadEla.value)
    : (isRoutePricingMath.value || isRoutePadMath.value))

  const isExpired = ref<boolean>(true)
  const deadline = computed<string | null>(() => upsellBannerStore.getDeadline)

  const { countdownText } = useTimeCountdown({ date: deadline }, {
    format: 'full',
    onStart: (diff) => {
      isExpired.value = diff <= 0
    },
    onComplete: () => {
      isExpired.value = true
    },
  })

  const isVisible = computed<boolean>(() => upsellBannerStore.hasBanner && !!href.value && !isUpsoldDisciplinePage.value && !isExpired.value)

  const analyticsPayload = () => ({
    name,
    type: upsellBannerStore.getBanner?.type,
    discipline: upsellBannerStore.getDiscipline,
    kid_id: upsellBannerStore.getKidId,
  })

  const setSubmit = async (): Promise<void> => {
    trackEvent(amplitudeConfig.events.offerBannerClick, analyticsPayload())

    const target = href.value

    await dismissUpsellBanner()
    upsellBannerStore.setBanner(null)

    if (target) await navigateTo(target)
  }

  watch(isVisible, (visible) => {
    if (!visible) return

    trackEvent(amplitudeConfig.events.offerBannerView, analyticsPayload())
  }, { immediate: true })

  return {
    title,
    titleMobile,
    isVisible,
    offer,
    ctaLabel,
    href,
    countdownText,
    setSubmit,
  }
}
